import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import usePageContent from "@/hooks/usePageContent";
import ScrollReveal from "@/components/ScrollReveal";
import StaggerContainer from "@/components/StaggerContainer";

// Import fallback images
import talent1 from "@/assets/talent-1.jpg";
import talent2 from "@/assets/talent-2.jpg";
import talent3 from "@/assets/talent-3.jpg";
import talent4 from "@/assets/talent-4.jpg";

const fallbackImages: Record<string, string> = {
  "shelley-cohen": talent1,
  "david-holton": talent2,
  "robin-guy": talent3,
  "renaud-timson": talent4,
};

const FeaturedTalent = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";
  const { localizedPath } = useLocalizedPath();
  const { getContent } = usePageContent("home");

  // Check if random featured is enabled
  const { data: randomFeaturedSetting } = useQuery({
    queryKey: ["random-featured-setting"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "random_featured_talent")
        .maybeSingle();
      if (error) throw error;
      return (data?.value as { enabled: boolean })?.enabled ?? false;
    },
  });

  const { data: talents, isLoading } = useQuery({
    queryKey: ["featured-talent", randomFeaturedSetting],
    queryFn: async () => {
      if (randomFeaturedSetting) {
        // Random mode: fetch all active talents and randomly select 4
        const { data, error } = await supabase
          .from("talent")
          .select("*")
          .eq("is_active", true);
        if (error) throw error;
        
        // Shuffle and take 4
        const shuffled = [...(data || [])].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, 4);
      } else {
        // Manual mode: use is_featured flag
        const { data, error } = await supabase
          .from("talent")
          .select("*")
          .eq("is_featured", true)
          .order("display_order")
          .limit(4);
        if (error) throw error;
        return data;
      }
    },
  });

  const getImageUrl = (t: { slug: string; image_url: string | null }) => {
    if (t.image_url && t.image_url.startsWith("http")) {
      return t.image_url;
    }
    return fallbackImages[t.slug] || talent1;
  };

  return (
    <section id="talent" className="section-padding bg-secondary">
      <div className="container-wide">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-10 sm:mb-16">
          <h2 className="heading-section mb-3 sm:mb-4 text-primary">
            {getContent("featured_title", t("featuredTalent.title"))}
          </h2>
          <p className="text-editorial max-w-xl mx-auto text-[0.9375rem] sm:text-base">
            {getContent("featured_subtitle", t("featuredTalent.subtitle"))}
          </p>
        </ScrollReveal>

        {/* Talent Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4 lg:gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3 sm:space-y-4">
                <Skeleton className="aspect-[4/5] w-full" />
                <Skeleton className="h-5 sm:h-6 w-3/4" />
                <Skeleton className="h-3 sm:h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : talents && talents.length > 0 ? (
          <StaggerContainer className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4 lg:gap-8" staggerDelay={0.1}>
            {talents.map((talent) => (
              <Link 
                to={localizedPath(`/talent/${talent.slug}`)} 
                key={talent.id} 
                className="group block"
                aria-label={t("featuredTalent.viewProfile", { name: talent.name })}
              >
                {/* Image Container */}
                <div className="relative aspect-[4/5] mb-3 sm:mb-5 overflow-hidden bg-muted">
                  <img
                    src={getImageUrl(talent)}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  {/* View profile overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-all duration-300" aria-hidden="true">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Eye size={16} className="sm:w-5 sm:h-5 text-foreground" />
                    </div>
                  </div>
                </div>
                
                {/* Info */}
                <h3 className="font-display text-base sm:text-lg font-medium mb-1 sm:mb-2 text-primary group-hover:text-primary/70 transition-colors line-clamp-1">{talent.name}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-1">
                  {talent.languages?.join(" · ")}
                </p>
              </Link>
            ))}
          </StaggerContainer>
        ) : null}

        {/* View All Button */}
        <ScrollReveal className="text-center mt-10 sm:mt-14" delay={0.4}>
          <Button asChild variant="sectionCta">
            <Link to={localizedPath("/talent")}>
              {getContent("featured_view_all", t("featuredTalent.viewAll"))}
            </Link>
          </Button>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default FeaturedTalent;
